import React, {
  useRef,
  useEffect,
  useLayoutEffect,
  useState,
  useContext,
} from "react";
import Timeline from "./Timeline";
import { TimelineHeader } from "./TimelineHeader";
import Sidebar from "./Sidebar";
import {
  getDaysInYear,
  getMonthsInRange,
  getNumberOfWeeksBetweenYears,
  getNumberOfWeeksInYear,
  getDaysBetweenYears,
  months,
  getTodayPositionForYearRange,
  getNumberOfQuartersBetweenYears,
} from "../utils/date";
import { startOfYear, differenceInDays, getMonth, getWeek } from "date-fns";
import { useFilteredScopes } from "../hooks/useFilteredTasks";
import { ResourceMoniteringContext } from "../../resourcePlanner/resourceMonitoring/ResourceContext";
import { Rnd } from "react-rnd";
import { getCookie, setCookie } from "../../../helpers/cookieUtils";

export const GanttChartContainer = ({
  scopes,
  setScopes,
  expandedScopes,
  toggleScope,
  expandedTaskGroups,
  toggleTaskGroup,
  viewType = "monthly",
  showTaskModal,
  setTaskStartDate,
  viewTypeKey,
  isHideScope,
  isHideTask,
  handleTaskModal,
  critical,
  count,
  setScopeItem,
  overviewType,
  isHeaderCollapsed = false
}) => {
  const { displayYearRange, baselineScopes, todayTrigger } = useContext(
    ResourceMoniteringContext
  );

  const containerRef = useRef(null);
  const sidebarRef = useRef(null);
  const timelineRef = useRef(null);
  const numberOfDaysBetweenYears = getDaysBetweenYears(displayYearRange.start, displayYearRange.end);
  const defaultSidebarWidth = 450;
  const defaultColumnWidths = {
    scopes: 170,
    completed: 40,
    duration: 80,
    startDate: 80,
    endDate: 80,
  };
  const columnRatios = {
    scopes: defaultColumnWidths.scopes / defaultSidebarWidth,
    completed: defaultColumnWidths.completed / defaultSidebarWidth,
    duration: defaultColumnWidths.duration / defaultSidebarWidth,
    startDate: defaultColumnWidths.startDate / defaultSidebarWidth,
    endDate: defaultColumnWidths.endDate / defaultSidebarWidth,
  };
  const [sidebarWidth, setSidebarWidth] = useState(
    parseInt(getCookie("sidebarWidth"), 10) || defaultSidebarWidth
  );
  const [columnWidths, setColumnWidths] = useState({
    scopes: parseInt(getCookie("scopesColumnWidth"), 10) || 170,
    completed: parseInt(getCookie("completedColumnWidth"), 10) || 40,
    duration: parseInt(getCookie("durationColumnWidth"), 10) || 80,
    startDate: parseInt(getCookie("startDateColumnWidth"), 10) || 80,
    endDate: 80,
  });

  const [hoveredRowId, setHoveredRowId] = useState(null);
  // useEffect(() => {
  //   if (sidebarWidth > 450) {
  //     setColumnWidths({
  //       scopes: sidebarWidth * columnRatios.scopes,
  //       completed: sidebarWidth * columnRatios.completed,
  //       duration: sidebarWidth * columnRatios.duration,
  //       startDate: sidebarWidth * columnRatios.startDate,
  //       endDate: sidebarWidth * columnRatios.endDate,
  //     });
  //   }
  // }, [sidebarWidth]);

  function calculatedColumnWidths() {
    const totalWidth =
      columnWidths.scopes +
      columnWidths.completed +
      columnWidths.duration +
      columnWidths.startDate +
      columnWidths.endDate;
    return totalWidth;
  }
  const ganttChartContainerRef = useRef(null);
  const { filteredScopes } = useFilteredScopes(scopes ?? []);
  const scrollSyncInProgress = useRef(false);

  const [taskDates, setTaskDates] = useState({});
  useEffect(() => {
    if (scopes.length) {
      setTaskDates(
        scopes
          .flatMap((scope) => [
            ...(scope.tasks || []),
            ...(scope.taskGroups || []).flatMap((group) => group.tasks || []),
          ])
          .reduce((acc, task) => {
            acc[task.id] = { startDate: task.startDate, endDate: task.endDate };
            return acc;
          }, {})
      );
    }
  }, [scopes]);

  const months = getMonthsInRange(displayYearRange.start, displayYearRange.end);
  const totalWidth = {
    monthly: months.length * 241,
    quarter: getNumberOfQuartersBetweenYears(displayYearRange.start, displayYearRange.end) * 501,
    weekly: getNumberOfWeeksBetweenYears(displayYearRange.start, displayYearRange.end) * 241,
    daily: numberOfDaysBetweenYears * 241,
  };

  useEffect(() => {
    const sidebar = sidebarRef.current;
    const timeline = timelineRef.current;
    const container = containerRef.current;

    if (!container || !sidebar || !timeline) return;

    const handleHorizontalScroll = (e) => {
      const scrollLeft = e.target.scrollLeft;
      container.scrollLeft = scrollLeft;
    };

    const handleVerticalScroll = (e) => {
      if (scrollSyncInProgress.current) return;

      scrollSyncInProgress.current = true;
      const scrollTop = e.target.scrollTop;

      if (e.target === sidebar) {
        timeline.scrollTop = scrollTop;
      } else if (e.target === timeline) {
        sidebar.scrollTop = scrollTop;
      }

      setTimeout(() => {
        scrollSyncInProgress.current = false;
      }, 10);
    };

    container.addEventListener("scroll", handleHorizontalScroll);
    sidebar.addEventListener("scroll", handleVerticalScroll);
    timeline.addEventListener("scroll", handleVerticalScroll);

    return () => {
      container.removeEventListener("scroll", handleHorizontalScroll);
      sidebar.removeEventListener("scroll", handleVerticalScroll);
      timeline.removeEventListener("scroll", handleVerticalScroll);
    };
  }, []);

  useLayoutEffect(() => {
    const calculateScrollPosition = () => {
      if (!containerRef.current) return;
      containerRef.current.scrollLeft = getTodayPositionForYearRange(viewType, displayYearRange?.start, displayYearRange?.end) - 300;
    };
    setTimeout(calculateScrollPosition, 100);
  }, [viewType, count, todayTrigger]);

  // Reset scroll position when baseline scopes change
  // useEffect(() => {
  //   if (sidebarRef.current && timelineRef.current) {
  //     sidebarRef.current.scrollTop = 0;
  //     timelineRef.current.scrollTop = 0;
  //   }
  // }, [baselineScopes]);

  return (
    <div
      className="relative flex w-full h-full"
      ref={ganttChartContainerRef}
      style={{
        height:
          overviewType === "Project"
            ? `calc(100vh - ${isHeaderCollapsed ? '268px' : '470px'})`
            : `calc(100vh - 211px)`,
      }}
    >
        <div
          className={` relative flex border-r border-[#342c82] w-full ${isHideScope && 'hidden'}`}
          style={{
            height:
              overviewType === "Project"
                ? `calc(100vh - ${isHeaderCollapsed ? '268px' : '470px'})`
                : `calc(100vh - 211px)`,
          }}
        >
          <Rnd
            default={{
              width: sidebarWidth,
            }}
            size={{ width: sidebarWidth }}
            bounds="window"
            enableResizing={{ right: true }}
            style={{
              position: "sticky",
              left: 0,
              cursor: "pointer",
            }}
            onResizeStop={(e, direction, ref) => {
              const newWidth = Number(ref.style.width.replace("px", ""));
              setSidebarWidth(newWidth);
              setCookie("sidebarWidth", newWidth);
            }}
          >
            <div className="flex flex-col w-full overflow-x-auto">
              <div
                className="h-[36px] bg-[#ece7fd] border-b border-[#342c82] flex items-center overflow-x-auto"
                style={{
                  minWidth: calculatedColumnWidths(),
                }}
              >
                <Rnd
                  default={{
                    width: columnWidths.scopes,
                  }}
                  size={{ width: columnWidths.scopes }}
                  bounds="window"
                  enableResizing={{ right: true }}
                  style={{
                    position: "sticky",
                    left: 0,
                    cursor: "pointer",
                    borderRight: "1px solid #342c82",
                  }}
                  onResizeStop={(e, direction, ref) => {
                    const newWidth = Number(ref.style.width.replace("px", ""));
                    setColumnWidths({
                      ...columnWidths,
                      scopes: newWidth > 170 ? newWidth : 170,
                    });
                    setCookie(
                      "scopesColumnWidth",
                      newWidth > 170 ? newWidth : 170
                    );
                  }}
                >
                  <p
                    className="h-[34px] flex justify-start items-center font-medium text-sm truncate pl-4"
                    style={{
                      width: columnWidths.scopes,
                    }}
                  >
                    Scope Items / Tasks
                  </p>
                </Rnd>
                <Rnd
                  default={{
                    width: columnWidths.completed,
                  }}
                  size={{ width: columnWidths.completed }}
                  bounds="window"
                  enableResizing={{ right: true }}
                  style={{
                    position: "sticky",
                    left: 0,
                    cursor: "pointer",
                    borderRight: "1px solid #342c82",
                  }}
                  onResizeStop={(e, direction, ref) => {
                    const newWidth = Number(ref.style.width.replace("px", ""));
                    setColumnWidths({
                      ...columnWidths,
                      completed: newWidth > 40 ? newWidth : 40,
                    });
                    setCookie(
                      "completedColumnWidth",
                      newWidth > 40 ? newWidth : 40
                    );
                  }}
                >
                  <p
                    className="h-[32px] px-2 flex justify-center items-center font-medium text-sm text-center truncate"
                    style={{
                      minWidth: columnWidths.completed,
                    }}
                  >
                    (%)
                  </p>
                </Rnd>
                <Rnd
                  default={{
                    width: columnWidths.duration,
                  }}
                  size={{ width: columnWidths.duration }}
                  bounds="window"
                  enableResizing={{ right: true }}
                  style={{
                    position: "sticky",
                    left: 0,
                    cursor: "pointer",
                    borderRight: "1px solid #342c82",
                  }}
                  onResizeStop={(e, direction, ref) => {
                    const newWidth = Number(ref.style.width.replace("px", ""));
                    setColumnWidths({
                      ...columnWidths,
                      duration: newWidth > 80 ? newWidth : 80,
                    });
                    setCookie(
                      "durationColumnWidth",
                      newWidth > 80 ? newWidth : 80
                    );
                  }}
                >
                  <p
                    className="h-[32px] px-2 flex justify-center items-center font-medium text-sm text-center truncate"
                    style={{
                      minWidth: columnWidths.duration,
                    }}
                  >
                    Duration
                  </p>
                </Rnd>
                <Rnd
                  default={{
                    width: columnWidths.startDate,
                  }}
                  size={{ width: columnWidths.startDate }}
                  bounds="window"
                  enableResizing={{ right: true }}
                  style={{
                    position: "sticky",
                    left: 0,
                    cursor: "pointer",
                    borderRight: "1px solid #342c82",
                  }}
                  onResizeStop={(e, direction, ref) => {
                    const newWidth = Number(ref.style.width.replace("px", ""));
                    setColumnWidths({
                      ...columnWidths,
                      startDate: newWidth > 80 ? newWidth : 80,
                    });
                    setCookie(
                      "startDateColumnWidth",
                      newWidth > 80 ? newWidth : 80
                    );
                  }}
                >
                  <p
                    className="h-[32px] px-2 flex justify-center items-center font-medium text-sm text-center truncate"
                    style={{
                      minWidth: columnWidths.startDate,
                    }}
                  >
                    Start Date
                  </p>
                </Rnd>
                <p
                  className="h-[32px] px-2 flex justify-center items-center font-medium text-sm text-center truncate"
                  style={{
                    minWidth: columnWidths.endDate,
                  }}
                >
                  End Date
                </p>
              </div>
              {viewType !== "daily" && (
                <div
                  className="h-[24px] border-b border-[#342c82] bg-[#ece7fd]"
                  style={{ minWidth: calculatedColumnWidths() }}
                ></div>
              )}
              <Sidebar
                reference={sidebarRef}
                scopes={filteredScopes}
                setScopes={setScopes}
                expandedScopes={expandedScopes}
                toggleScope={toggleScope}
                expandedTaskGroups={expandedTaskGroups}
                toggleTaskGroup={toggleTaskGroup}
                showTaskModal={showTaskModal}
                taskDates={taskDates}
                setTaskDates={setTaskDates}
                viewType={viewType}
                sidebarWidth={calculatedColumnWidths()}
                columnWidths={columnWidths}
                handleTaskModal={handleTaskModal}
                setScopeItem={setScopeItem}
                overviewType={overviewType}
                isHeaderCollapsed={isHeaderCollapsed}
                isHideTask={isHideTask}
                hoveredRowId={hoveredRowId} 
                setHoveredRowId={setHoveredRowId}
              />
            </div>
          </Rnd>
        </div>


      <div
        style={{
          width: !isHideScope ? `calc(100% - ${sidebarWidth}px)` : "100%",
          display: "flex",
          position: "sticky",
        }}
      >
        <div ref={containerRef} className="timeline-scroll overflow-x-auto overflow-y-hidden">
          <div style={{ width: `${totalWidth[viewType]}px` }}>
            <div
              className={`${(viewType === "weekly" ||viewType === "quarter") ? "h-[60px]" : "h-[36px]"
                } bg-[#ece7fd] flex border-b border-[#342c82]`}
            >
              <TimelineHeader
                viewType={viewType}
                displayYearRange={displayYearRange}
              />
            </div>
            {viewType === "monthly" && (
              <div className="h-[24px] bg-[#ece7fd] border-b border-[#342c82]">
                <TimelineHeader
                  viewType={viewType}
                  subHeader={true}
                  displayYearRange={displayYearRange}
                />
              </div>
            )}
            <Timeline
              reference={timelineRef}
              scopes={filteredScopes}
              expandedScopes={expandedScopes}
              expandedTaskGroups={expandedTaskGroups}
              viewType={viewType}
              showTaskModal={showTaskModal}
              setTaskStartDate={setTaskStartDate}
              taskDates={taskDates}
              setTaskDates={setTaskDates}
              handleTaskModal={handleTaskModal}
              critical={critical}
              displayYearRange={displayYearRange}
              overviewType={overviewType}
              isHeaderCollapsed={isHeaderCollapsed}
              isHideTask={isHideTask}
              hoveredRowId={hoveredRowId} 
              setHoveredRowId={setHoveredRowId}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
